// AI Generated Test
import { Component, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';
import { CrawlsRepoService } from '@client/app/services/api/crawls-repo/crawls-repo.service';
import { QASpellcheckLineChartService } from '@client/app/blocks/service/charts/qa-spellcheck-line-chart.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { BaseContainerComponentController } from '@client/app/blocks/base/base.controller';
import { safeClone } from '@client/app/shared/utils/object-utils';

@Component({
    selector: 'spelling-dashboard',
    standalone: false,
    template: `
        <page-header
                [icon]="['QA', 'SPELLCHECK_SUMMARY'] | monIcons"
                [header]="'Misspellings Summary' | translate">
                <page-header-content>
                    <div class="p-1 pull-right">
                        <csv-export-button clsSize="py-2 px-3" clsMargin="mr-0" [exports]="exports"></csv-export-button>
                    </div>
                </page-header-content>
        </page-header>
        <div class="spelling-dashboard">
            <div class="row">
                <div class="col-48">
                    <div class="card panel-monsido-card">
                        <mon-panel-header [pHeader]="'Misspellings history' | translate"></mon-panel-header>
                        <div class="card-body">
                            <div class="row outer-b-xlarge" *ngIf="loading">
                                <div class="col-48 text-center">
                                    <span [ngClass]="['INVENTORY', 'DASHBOARD', 'RUNNING'] | monIcons" class="fa-spin fa-3x fa-fw" aria-hidden="true"></span>
                                </div>
                            </div>

                            <div class="row pt-4" *ngIf="!loading">
                                <div class="col-lg-16 d-flex flex-column align-item-center justify-content-center">
                                    <notification class="mb-6"
                                                  classColor="text-link"
                                                  [link]="'base.customer.domain.qa.spellcheck.confirmed'"
                                                  [faIcon]="(['INVENTORY', 'DASHBOARD', 'MISSPELLINGS'] | monIcons) + ' fa-3x'"
                                                  [title]="'Unique misspellings found' | translate"
                                                  [monAriaIconText]="'Misspellings' | translate"
                                                  [monAriaButtonText]="'Go to misspellings' | translate"
                                                  [digit]="summary?.confirmed_misspellings">
                                    </notification>
                                    <notification class="mb-6"
                                                  classColor="text-link"
                                                  [link]="'base.customer.domain.qa.spellcheck.potential'"
                                                  [faIcon]="(['INVENTORY', 'DASHBOARD', 'MISSPELLINGS'] | monIcons) + ' fa-3x'"
                                                  [title]="'Potential misspellings found' | translate"
                                                  [monAriaIconText]="'Potential misspellings' | translate"
                                                  [monAriaButtonText]="'Go to potential misspellings' | translate"
                                                  [digit]="summary?.potential_misspellings">
                                    </notification>
                                    <notification class="mb-6"
                                                  classColor="text-link"
                                                  [faIcon]="(['INVENTORY', 'DASHBOARD', 'LANGUAGE'] | monIcons) + ' fa-3x'"
                                                  [title]="'Languages found' | translate"
                                                  [monAriaIconText]="'Languages' | translate"
                                                  [digit]="summary?.languages">
                                    </notification>
                                    <notification-text class="mb-6 common-language-block"
                                                       classColor="text-link"
                                                       [faIcon]="(['INVENTORY', 'DASHBOARD', 'LANGUAGE'] | monIcons) + ' fa-3x'"
                                                       [title]="'Most common language' | translate"
                                                       [text]="commonLanguage | translateLanguage">
                                    </notification-text>

                                </div>
                                <div class="col-lg-32 pr-lg-9">
                                    <mon-chart-line [emptyDataMessage]="'There is no historic data for domain groups' | translate"
                                                [height]="300"
                                                [data]="data"
                                                [labels]="labels"
                                                [colors]="colors"
                                                [options]="options"
                                                [override]="overrides"
                                                [series]="series">
                                    </mon-chart-line>
                                    <div class="clear"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-lg-24 col-md-48">
                    <spelling-dashboard-misspellings [header]="'Most common misspellings' | translate"
                                                     classification="confirmed"></spelling-dashboard-misspellings>
                </div>

                <div class="col-lg-24 col-md-48">
                    <spelling-dashboard-misspellings [header]="'Most common potential misspellings' | translate"
                                                     classification="potential"></spelling-dashboard-misspellings>
                </div>
            </div>
        </div>
    `,
})
export class SpellingDashboardComponent extends BaseContainerComponentController implements OnInit {
    summary: any;
    loading = false;
    series: any;
    colors: any;
    options: any;
    overrides: any;
    data: any;
    override labels: any;
    commonLanguage: any;
    exports: any;

    constructor (
        private stateService: StateService,
        private crawlsRepoService: CrawlsRepoService,
        private qaSpellcheckLineChartService: QASpellcheckLineChartService,
        private translateService: TranslateService,
    ) {
        super({ table: true });
        this.getPage = this.activate.bind(this);
    }

    ngOnInit () {
        this.activate();
    }

    activate () {
        const lineChartService = this.qaSpellcheckLineChartService.init();
        this.loading = true;
        this.series = lineChartService.getSeries();
        this.colors = lineChartService.getColors();
        this.options = lineChartService.getOption();
        this.overrides = lineChartService.getOverride();

        const promises: any[] = [];
        this.exports = [
            {
                name: this.translateService.getString('Misspellings Summary'),
                data: {
                    category: 'spellcheck_summary',
                },
            },
        ];
        promises.push(this.getSummary());
        promises.push(this.getHistory());
        promises.push(this.getLanguages());

        Promise.all(promises)
            .then((data: any) => {
                this.summary = data[0];
                const plainData = data[1].reverse();
                this.labels = lineChartService.getLabels(plainData);
                this.data = lineChartService.getData(plainData);

                this.commonLanguage = this.getCommonLanguages(data[2]);
            })
            .catch(() => {
                // Handle error if needed
            })
            .finally(() => {
                this.loading = false;
            });
    }

    getSummary () {
        return this.spellingRepoService.getSummary({ domain_id: this.stateService.params.domainId });
    }

    override getLanguages () {
        return this.spellingRepoService.getAllLanguages({ domain_id: this.stateService.params.domainId });
    }

    getCommonLanguages (langArray: any) {
        // Sorts array so the entry with highest html_page_count & document_count are first
        const languages = safeClone(langArray).sort((a: any, b: any) => {
            return a.html_page_count > b.html_page_count ? -1 : b.html_page_count > a.html_page_count ? 1 : 0;
        });
        return languages[0].lang;
    }

    getHistory () {
        const params = {
            page_size: 5,
        };

        return this.crawlsRepoService.getAll(params);
    }
}
